import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  StellarFate,
  StellarSettings,
  StellarStage,
  StellarState,
} from "../shared/stellar-types";
import {
  automationRate,
  isStageReady,
  isUpgradeUnlocked,
  pulsePower,
  stageProgressRatio,
  stageThreshold,
  STELLAR_CONTRACTS,
  STELLAR_LEGACY_NODES,
  STELLAR_MASSES,
  STELLAR_STAGES,
  STELLAR_UPGRADES,
  stellarDustYield,
  upgradeCost,
} from "../stellar-engine";
import { useStellar } from "./StellarContext";
import { HrDiagram, StageTrack, StellarCrossSection } from "./StellarVisuals";

const stageOrder: StellarStage[] = ["cloud", "protostar", "mainSequence", "giant", "terminal", "remnant"];
const gravityClockUpgrade = STELLAR_UPGRADES.find((upgrade) => upgrade.id === "gravityClock")!;
const compositionLabels = {
  hydrogen: "氢",
  helium: "氦",
  carbonOxygen: "碳 / 氧",
  silicon: "硅族",
  iron: "铁族",
} as const;

export function StellarCorePage() {
  const game = useStellar();
  const state = game.state;
  if (!state) return null;
  if (!state.star) return <MassSelection />;

  const stage = STELLAR_STAGES[state.stage];
  const progress = stageProgressRatio(state);
  const ready = isStageReady(state);
  const compact = state.settings.compactNumbers;
  const firstPulseComplete = state.tutorial.firstPulseComplete;
  const beforeMainSequence = state.stage === "cloud" || state.stage === "protostar";
  const currentAutomationRate = automationRate(state);
  const automationStatus = ready ? "waiting" : currentAutomationRate > 0 ? "running" : "paused";
  const gravityClockLevel = state.upgrades.gravityClock ?? 0;
  const gravityClockCost = upgradeCost(state, gravityClockUpgrade);
  const gravityClockMaxed = gravityClockLevel >= gravityClockUpgrade.maxLevel;

  return (
    <div className="stellar-page stellar-core-page" data-page="stellar-core" data-onboarding={firstPulseComplete ? "expanded" : "first-pulse"}>
      <PageHeading
        eyebrow={`GENERATION ${String(state.meta.generation).padStart(2, "0")} // ${stage.name}`}
        title={stageTitle(state)}
        description={!firstPulseComplete ? "自治观测正在推进；点一次“演化脉冲”校准剖面并加速本阶段。" : ready ? "本阶段进度已满。点击确认后，恒星结构与 H–R 位置会发生可见跃迁。" : stage.objective}
      />

      {state.stage !== "remnant" && (
        <section
          className="stellar-automation-strip"
          aria-label="自治观测"
          data-automation-status={automationStatus}
          data-automation-rate={currentAutomationRate}
          data-upgrade-id="gravityClock"
        >
          <div className="stellar-automation-state"><i aria-hidden="true" /><span><b>{automationStatus === "running" ? "自治观测运行中" : automationStatus === "waiting" ? "自治观测等待跃迁" : "自治观测已暂停"}</b><small>{automationStatus === "waiting" ? "确认阶段后继续积累进度" : "不点击也会持续积累阶段进度"}</small></span></div>
          <strong>{formatNumber(currentAutomationRate, compact)}<small>/秒</small></strong>
          <div className="stellar-automation-next"><span>引力时钟 LV {gravityClockLevel}/{gravityClockUpgrade.maxLevel}</span><small>{gravityClockMaxed ? "已达最高档" : `下一档 +0.45/秒 · ${gravityClockCost} 星光`}</small></div>
          <button
            type="button"
            className="button"
            data-stellar-action="BUY_UPGRADE"
            data-upgrade-id="gravityClock"
            onClick={() => void game.perform({ type: "BUY_UPGRADE", upgradeId: "gravityClock" })}
            disabled={game.busy || !isUpgradeUnlocked(state, gravityClockUpgrade) || gravityClockMaxed || state.resources.starlight < gravityClockCost}
          >
            {gravityClockMaxed ? "已满级" : `${gravityClockCost} 星光 · 提升`}
          </button>
        </section>
      )}

      {firstPulseComplete && <StageTrack stage={state.stage} />}

      <section className="stellar-observatory" aria-label="恒星演化观测台">
        <StellarCrossSection state={state} pulseEcho={game.pulseEcho} />
        {firstPulseComplete && !beforeMainSequence && (
          <HrDiagram
            temperatureK={state.star.surfaceTemperatureK}
            luminositySolar={state.star.luminositySolar}
            pointLabel="当前星"
          />
        )}
      </section>

      {state.stage !== "remnant" && <section className="stellar-operation" aria-labelledby="stellar-operation-heading">
        <div className="stellar-operation-copy">
          <p className="stellar-kicker">{firstPulseComplete ? "CURRENT OPERATION" : "FIRST MANUAL PULSE"}</p>
          <h2 id="stellar-operation-heading">{!firstPulseComplete ? "点一下，让模型响应你的观测" : ready ? "阶段边界已锁定" : "推进一段可观测演化"}</h2>
          {firstPulseComplete && <p>{ready ? stage.science : pulseExplanation(state)}</p>}
        </div>
        <div className="stellar-progress" data-stage-progress={Math.round(progress * 100)}>
          <div>
            <span>阶段进度</span>
            <b>{formatNumber(state.stageProgress, compact)} / {formatNumber(stageThreshold(state), compact)}</b>
          </div>
          <div className="stellar-meter" role="progressbar" aria-label={`${stage.name}阶段进度`} aria-valuemin={0} aria-valuemax={stageThreshold(state)} aria-valuenow={state.stageProgress}>
            <span style={{ width: `${progress * 100}%` }} />
          </div>
          <small>每次脉冲推进 {formatNumber(pulsePower(state), compact)} · 自动观测 {formatNumber(automationRate(state), compact)}/秒</small>
        </div>
        <div className="stellar-primary-actions">
          {!ready ? (
            <button
              type="button"
              className="stellar-pulse-button"
              data-stellar-action="PULSE"
              onClick={game.pulse}
              disabled={game.busy}
            >
              <span aria-hidden="true">◉</span>
              <b>演化脉冲 · 点击加速</b>
              <small>{game.queuedPulses ? `${game.queuedPulses} 次正在写入` : `主动推进 ${formatNumber(pulsePower(state), compact)}`}</small>
            </button>
          ) : (
            <button
              type="button"
              className="stellar-pulse-button advance"
              data-stellar-action="ADVANCE_STAGE"
              onClick={() => void game.perform({ type: "ADVANCE_STAGE" })}
              disabled={game.busy}
            >
              <span aria-hidden="true">↗</span>
              <b>确认阶段跃迁</b>
              <small>{`进入${nextStageName(state.stage)}`}</small>
            </button>
          )}
        </div>
      </section>}

      {state.stage === "remnant" ? (
        <>
          <EvidenceDashboard state={state} beforeMainSequence={false} />
          <CompositionLedger state={state} />
          <RemnantEnding />
        </>
      ) : firstPulseComplete && (
        <details className="stellar-core-drawer">
          <summary>
            <span><b>观测数据与仪器</b><small>温度、光度、成分与升级</small></span>
            <em>{formatNumber(state.resources.insight, compact)} 见解 · {formatNumber(state.resources.starlight, compact)} 星光</em>
          </summary>
          <div className="stellar-core-drawer-body">
            <EvidenceDashboard state={state} beforeMainSequence={beforeMainSequence} />
            <CompositionLedger state={state} />
            <UpgradeDeck />
          </div>
        </details>
      )}
    </div>
  );
}

export function StellarArchivePage() {
  const state = useStellar().state;
  if (!state) return null;
  return (
    <div className="stellar-page stellar-archive-page" data-page="stellar-archive">
      <PageHeading eyebrow="OBSERVATION ARCHIVE" title="每一颗星都留下轨迹" description="这里保留阶段跃迁、升级、资源与跨代遗产。" />
      <section className="stellar-archive-summary" aria-label="星核档案摘要">
        <EvidenceCard label="当前世代" value={`第 ${state.meta.generation} 代`} detail={`${state.meta.completedStars} 颗恒星已闭合账本`} id="generation" />
        <EvidenceCard label="累计脉冲" value={formatNumber(state.meta.totalPulses, true)} detail="主动观测推进" id="pulses" />
        <EvidenceCard label="星尘记忆" value={formatNumber(state.meta.stardust, true)} detail="跨代保留" id="stardust" />
        <EvidenceCard label="抛射重元素" value={formatNumber(state.meta.ejectedMetals, true)} detail="解锁跨代遗产节点" id="ejected-metals" />
        <EvidenceCard label="已完成委托" value={`${state.meta.completedContracts} 项`} detail="选择本代首选质量路线" id="contracts" />
        <EvidenceCard label="遗产节点" value={`${state.meta.legacyUnlocks.length}/${STELLAR_LEGACY_NODES.length}`} detail="由累计重元素自动激活" id="legacy-unlocks" />
        <EvidenceCard label="已发现结构" value={`${state.discoveries.length} 项`} detail="来自真实阶段变化" id="discoveries" />
      </section>

      <section className="stellar-panel" aria-labelledby="history-heading">
        <div className="stellar-section-heading"><div><p className="stellar-kicker">CAUSAL HISTORY</p><h2 id="history-heading">恒星轨迹</h2></div></div>
        {state.history.length ? (
          <ol className="stellar-history">
            {[...state.history].reverse().map((entry) => (
              <li key={entry.id} data-history-stage={entry.stage}>
                <span aria-hidden="true" />
                <div><small>第 {entry.generation} 代 · {STELLAR_STAGES[entry.stage].name}</small><b>{entry.title}</b><p>{entry.detail}</p></div>
              </li>
            ))}
          </ol>
        ) : <p className="stellar-empty">第一条轨迹会从分子云开始。</p>}
      </section>

      <LegacyProgress state={state} />

      <section className="stellar-panel stellar-science-notes" aria-labelledby="science-notes-heading">
        <div className="stellar-section-heading"><div><p className="stellar-kicker">MODEL BOUNDARY</p><h2 id="science-notes-heading">哪些是真实关系，哪些是游戏抽象</h2></div></div>
        <dl>
          <div><dt>观测确认</dt><dd>质量影响核心条件；高质量主序星通常更亮、寿命更短；H–R 图横轴左热右冷、纵轴向上更亮。</dd></div>
          <div><dt>标准简化模型</dt><dd>单星按分子云、原恒星、主序、巨星、终末和遗迹分段；首发以约 8 M☉ 作为两条终局路线的简化边界。质量账本是忽略辐射与中微子质量亏损的重子物质近似。</dd></div>
          <div><dt>仍有不确定性</dt><dd>真实恒星质量损失、旋转、金属丰度和双星相互作用都会改变精确边界与轨迹。</dd></div>
          <div><dt>游戏虚构设定</dt><dd>“演化脉冲”“见解”“星尘记忆”是操作与进度语言；“星光”是归一化测光资源，不是真实能量单位。</dd></div>
        </dl>
        <details>
          <summary>查看权威资料入口</summary>
          <ul>
            <li><a href="https://science.nasa.gov/universe/stars/" target="_blank" rel="noreferrer">NASA Science：恒星基础与生命周期</a></li>
            <li><a href="https://science.nasa.gov/universe/stars/types/" target="_blank" rel="noreferrer">NASA Science：恒星类型</a></li>
            <li><a href="https://imagine.gsfc.nasa.gov/educators/lifecycles/LC_main_p8.html" target="_blank" rel="noreferrer">NASA/GSFC：H–R 图教学资料</a></li>
          </ul>
        </details>
      </section>
    </div>
  );
}

export function StellarSettingsPage() {
  const game = useStellar();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<StellarSettings | null>(game.state?.settings ?? null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  useEffect(() => { if (game.state) setSettings({ ...game.state.settings }); }, [game.state?.settings]);
  if (!game.state || !settings) return null;

  async function resetProgress() {
    const confirmed = window.confirm("只会删除星核纪元进度；主游戏存档与账号不会改变。确认重置？");
    if (!confirmed) return;
    setResetting(true);
    setResetError(null);
    const success = await game.resetProgress();
    if (success) navigate("/stellar", { replace: true });
    else setResetError("重置未完成，请稍后重试。");
    setResetting(false);
  }

  return (
    <div className="stellar-page" data-page="stellar-settings">
      <PageHeading eyebrow="LOCAL OBSERVATORY CONTROL" title="星核界面设置" description="这些偏好只作用于星核纪元，不改变星际殖民存档的显示方式。" />
      <div className="stellar-settings-grid">
        <section className="stellar-panel" aria-labelledby="stellar-display-heading">
          <div className="stellar-section-heading"><div><p className="stellar-kicker">DISPLAY</p><h2 id="stellar-display-heading">显示与动态</h2></div></div>
          <form className="stellar-form" onSubmit={(event) => { event.preventDefault(); void game.perform({ type: "CHANGE_STELLAR_SETTINGS", settings }); }}>
            <label className="stellar-check-row">
              <input type="checkbox" checked={settings.reducedMotion} onChange={(event) => setSettings({ ...settings, reducedMotion: event.target.checked })} />
              <span><b>减少动态效果</b><small>关闭脉冲扩散和界面过渡；系统设置仍然优先。</small></span>
            </label>
            <label className="stellar-check-row">
              <input type="checkbox" checked={settings.compactNumbers} onChange={(event) => setSettings({ ...settings, compactNumbers: event.target.checked })} />
              <span><b>紧凑数字</b><small>较大数值显示为“万 / 亿”等紧凑形式，物理单位不会省略。</small></span>
            </label>
            <button className="button primary" data-stellar-action="CHANGE_STELLAR_SETTINGS" disabled={game.busy || sameSettings(settings, game.state.settings)}>保存星核设置</button>
          </form>
        </section>
        <section className="stellar-panel" aria-labelledby="stellar-controls-heading">
          <div className="stellar-section-heading"><div><p className="stellar-kicker">CONTROLS</p><h2 id="stellar-controls-heading">操作与保存</h2></div></div>
          <ul className="stellar-definition-list">
            <li><b>点击与键盘</b><span>所有操作都是原生按钮，可用 Tab 聚焦、Enter 或空格执行。</span></li>
            <li><b>实时存档</b><span>脉冲约每 0.75 秒合并写入；升级和阶段跃迁立即排队。</span></li>
            <li><b>离线推进</b><span>服务器按最后结算时间推进自动观测，并在阶段边界停止。</span></li>
            <li><b>账号共享</b><span>登录、密码与会话属于同一账号；两款游戏的资源和进度彼此隔离。</span></li>
          </ul>
        </section>
        <section className="stellar-panel danger-zone" aria-labelledby="stellar-reset-heading">
          <div className="stellar-section-heading"><div><p className="stellar-kicker">RESET</p><h2 id="stellar-reset-heading">重置星核纪元</h2></div></div>
          <p>删除当前账号的星核世代、升级和星尘，从第一颗太阳级恒星重新开始。主游戏存档、账号和登录状态不会改变。</p>
          <button type="button" className="button danger-button" data-stellar-action="RESET_STELLAR" onClick={() => void resetProgress()} disabled={resetting}>
            {resetting ? "正在安全重置…" : "重置星核纪元进度"}
          </button>
          {resetError && <p className="danger-text" role="alert">{resetError}</p>}
        </section>
      </div>
    </div>
  );
}

function MassSelection() {
  const game = useStellar();
  const state = game.state!;
  const contract = STELLAR_CONTRACTS[state.meta.contractId];
  return (
    <div className="stellar-page stellar-mass-selection" data-page="stellar-core" data-contract-id={contract.id}>
      <PageHeading
        eyebrow={`GENERATION ${String(state.meta.generation).padStart(2, "0")} // MASS CONTRACT`}
        title={`为第 ${state.meta.generation} 代选择恒星质量`}
        description={`${contract.name}：${contract.brief}`}
      />
      <section className="stellar-mass-comparison" aria-label="候选恒星质量对照">
        {Object.values(STELLAR_MASSES).map((mass) => (
          <article
            key={mass.id}
            className={`stellar-mass-card mass-${mass.id}`}
            data-mass-id={mass.id}
            data-contract-preferred={String(contract.preferredMass === mass.id)}
          >
            <div className="stellar-mass-orb" aria-hidden="true"><span /></div>
            <header><span>{mass.shortName}</span><h2>{mass.name}</h2><p>{mass.strategy}</p></header>
            <dl>
              <div><dt>主序光度</dt><dd>{formatNumber(mass.mainLuminositySolar, true)} L☉</dd></div>
              <div><dt>模型寿命</dt><dd>{formatYears(mass.lifetimeYears)}</dd></div>
              <div><dt>终局路线</dt><dd>{fateLabel(mass.fate)}</dd></div>
            </dl>
            <button className="button primary full" data-stellar-action="SELECT_STAR" data-mass-id={mass.id} onClick={() => void game.perform({ type: "SELECT_STAR", massId: mass.id })} disabled={game.busy}>选择 {mass.shortName} · +{contract.rewards[mass.id]} 星尘{contract.preferredMass === mass.id ? " · 首选" : ""}</button>
          </article>
        ))}
      </section>
    </div>
  );
}

function UpgradeDeck() {
  const game = useStellar();
  const state = game.state!;
  const upgrades = STELLAR_UPGRADES.filter((upgrade) => isUpgradeUnlocked(state, upgrade) && !upgrade.permanent);
  return (
    <section className="stellar-panel stellar-upgrades" aria-labelledby="stellar-upgrades-heading">
      <div className="stellar-section-heading">
        <div><p className="stellar-kicker">OBSERVATORY SYSTEMS</p><h2 id="stellar-upgrades-heading">让观测方式改变同一颗星</h2></div>
        <span>{formatNumber(state.resources.insight, state.settings.compactNumbers)} 见解 · {formatNumber(state.resources.starlight, state.settings.compactNumbers)} 星光</span>
      </div>
      <div className="stellar-upgrade-grid">
        {upgrades.map((upgrade) => {
          const level = state.upgrades[upgrade.id] ?? 0;
          const cost = upgradeCost(state, upgrade);
          const maxed = level >= upgrade.maxLevel;
          const currency = currencyLabel(upgrade.currency);
          const available = currencyBalance(state, upgrade.currency);
          return (
            <article key={upgrade.id} className="stellar-upgrade" data-upgrade-id={upgrade.id} data-upgrade-level={level} data-upgrade-currency={upgrade.currency}>
              <header><span>LV {level}/{upgrade.maxLevel} · {currency}</span><h3>{upgrade.name}</h3></header>
              <p>{upgrade.description}</p>
              <small>{upgrade.effect}</small>
              <button className="button" data-stellar-action="BUY_UPGRADE" onClick={() => void game.perform({ type: "BUY_UPGRADE", upgradeId: upgrade.id })} disabled={game.busy || maxed || available < cost}>{maxed ? "已完成" : `${cost} ${currency} · 升级`}</button>
            </article>
          );
        })}
      </div>
      {!state.tutorial.firstUpgradeComplete && upgrades.length > 0 && <p className="stellar-inline-coach">第一次升级会立即改变脉冲、自动观测或资源收益；仪器不会改变恒星本身的质量定律。</p>}
    </section>
  );
}

function CompositionLedger({ state }: { state: StellarState }) {
  const star = state.star!;
  return (
    <section className="stellar-panel stellar-composition" aria-labelledby="composition-heading" data-evidence="core-composition">
      <div className="stellar-section-heading"><div><p className="stellar-kicker">CORE COMPOSITION</p><h2 id="composition-heading">核心成分账本</h2></div><span>归一化比例</span></div>
      <div className="stellar-composition-bar" aria-hidden="true">
        {Object.entries(star.composition).map(([id, value]) => <span key={id} className={`element-${id}`} style={{ width: `${value * 100}%` }} />)}
      </div>
      <dl className="stellar-composition-values">
        {(Object.entries(star.composition) as Array<[keyof typeof compositionLabels, number]>).map(([id, value]) => (
          <div key={id} data-element={id}><dt>{compositionLabels[id]}</dt><dd>{formatPercent(value)}</dd></div>
        ))}
      </dl>
      <p>{compositionExplanation(state)}</p>
    </section>
  );
}

function EvidenceDashboard({ state, beforeMainSequence }: { state: StellarState; beforeMainSequence: boolean }) {
  const compact = state.settings.compactNumbers;
  const star = state.star!;
  return (
    <section className="stellar-evidence-grid" aria-label="当前物理数据">
      <EvidenceCard label="初始质量" value={`${formatNumber(star.massSolar, compact)} M☉`} detail="太阳质量单位" id="mass" />
      <EvidenceCard
        label={state.stage === "cloud" ? "云核 / 模型温度" : state.stage === "protostar" ? "原恒星模型温度" : "表面温度"}
        value={`${formatNumber(star.surfaceTemperatureK, compact)} K`}
        detail={beforeMainSequence ? "尚不作为正式 H–R 恒星点" : "决定恒星颜色与 H–R 横坐标"}
        id="temperature"
      />
      <EvidenceCard label="相对光度" value={`${formatNumber(star.luminositySolar, compact)} L☉`} detail="太阳总光度单位" id="luminosity" />
      <EvidenceCard label="相对半径" value={`${formatNumber(star.radiusSolar, compact)} R☉`} detail="阶段化近似" id="radius" />
    </section>
  );
}

function LegacyProgress({ state }: { state: StellarState }) {
  return (
    <section className="stellar-panel stellar-legacy" aria-labelledby="stellar-legacy-heading" data-evidence="legacy-tree">
      <div className="stellar-section-heading">
        <div><p className="stellar-kicker">INTERSTELLAR LEGACY</p><h2 id="stellar-legacy-heading">抛射重元素形成跨代基础设施</h2></div>
        <span>{formatNumber(state.meta.ejectedMetals, true)} 重元素</span>
      </div>
      <p className="stellar-legacy-intro">每颗恒星闭合质量账本时，抛射物中的重元素会累计；达到阈值后自动解锁永久节点，不会因播种下一代而清零。</p>
      <div className="stellar-legacy-grid">
        {STELLAR_LEGACY_NODES.map((node) => {
          const unlocked = state.meta.legacyUnlocks.includes(node.id);
          const progress = Math.min(1, state.meta.ejectedMetals / node.metalsRequired);
          return (
            <article key={node.id} className="stellar-legacy-node" data-legacy-id={node.id} data-legacy-status={unlocked ? "unlocked" : "locked"}>
              <header><span>{unlocked ? "已激活" : `${formatNumber(state.meta.ejectedMetals, true)}/${node.metalsRequired}`}</span><h3>{node.name}</h3></header>
              <p>{node.effect}</p>
              <div className="stellar-meter" role="progressbar" aria-label={`${node.name}重元素进度`} aria-valuemin={0} aria-valuemax={node.metalsRequired} aria-valuenow={Math.min(state.meta.ejectedMetals, node.metalsRequired)}>
                <span style={{ width: `${progress * 100}%` }} />
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function RemnantEnding() {
  const game = useStellar();
  const state = game.state!;
  const star = state.star!;
  const permanent = STELLAR_UPGRADES.filter((upgrade) => upgrade.permanent);
  return (
    <section className="stellar-remnant" aria-labelledby="remnant-heading" data-overall-ending="true">
      <header>
        <p className="stellar-kicker">MASS LEDGER CLOSED</p>
        <h2 id="remnant-heading">一颗恒星结束了，但物质没有消失</h2>
        <p>{fateLabel(star.fate)}保留致密核心；其余物质回到星际介质，并转化为下一代可用的“星尘记忆”。</p>
      </header>
      <div className="stellar-mass-ledger" aria-label="恒星质量守恒账本">
        <span><small>初始质量</small><b>{formatNumber(star.massSolar, false)} M☉</b></span>
        <i aria-hidden="true">=</i>
        <span><small>遗迹质量</small><b>{formatNumber(star.remnantMassSolar, false)} M☉</b></span>
        <i aria-hidden="true">+</i>
        <span><small>抛射质量</small><b>{formatNumber(star.ejectaMassSolar, false)} M☉</b></span>
      </div>
      <LegacyProgress state={state} />

      <section className="stellar-panel stellar-permanent-upgrades" aria-labelledby="permanent-heading">
        <div className="stellar-section-heading"><div><p className="stellar-kicker">GENERATION MEMORY</p><h2 id="permanent-heading">把本代成果留给下一颗星</h2></div><span>预计本代 +{stellarDustYield(state)} 星尘</span></div>
        <div className="stellar-upgrade-grid">
          {permanent.map((upgrade) => {
            const level = state.permanentUpgrades[upgrade.id] ?? 0;
            const cost = upgradeCost(state, upgrade);
            const available = currencyBalance(state, upgrade.currency);
            const currency = currencyLabel(upgrade.currency);
            return (
              <article key={upgrade.id} className="stellar-upgrade" data-upgrade-id={upgrade.id} data-upgrade-level={level} data-upgrade-currency={upgrade.currency}>
                <header><span>永久 {level}/{upgrade.maxLevel}</span><h3>{upgrade.name}</h3></header>
                <p>{upgrade.description}</p><small>{upgrade.effect}</small>
                <button className="button" data-stellar-action="BUY_UPGRADE" onClick={() => void game.perform({ type: "BUY_UPGRADE", upgradeId: upgrade.id })} disabled={game.busy || level >= upgrade.maxLevel || available < cost}>{level >= upgrade.maxLevel ? "已完成" : `${cost} ${currency} · 写入`}</button>
              </article>
            );
          })}
        </div>
      </section>

      <div className="stellar-ending-actions">
        <button className="stellar-pulse-button advance" data-stellar-action="PRESTIGE" onClick={() => void game.perform({ type: "PRESTIGE" })} disabled={game.busy}><span aria-hidden="true">∞</span><b>播种第 {state.meta.generation + 1} 代</b><small>保留星尘、发现与跨代遗产</small></button>
      </div>
    </section>
  );
}

function EvidenceCard({ label, value, detail, id }: { label: string; value: string; detail: string; id: string }) {
  return <article className="stellar-evidence-card" data-evidence={id}><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>;
}

function PageHeading({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return <header className="stellar-page-heading"><p className="stellar-kicker">{eyebrow}</p><h1>{title}</h1><p>{description}</p></header>;
}

function currencyBalance(state: StellarState, currency: "insight" | "starlight" | "stardust") {
  return currency === "stardust" ? state.meta.stardust : state.resources[currency];
}

function currencyLabel(currency: "insight" | "starlight" | "stardust") {
  if (currency === "insight") return "见解";
  if (currency === "starlight") return "星光";
  return "星尘";
}

function stageTitle(state: StellarState) {
  if (state.stage === "giant" && state.star?.massId === "titan") return "红超巨星正在构造更深的燃烧层";
  if (state.stage === "terminal") return state.star?.fate === "coreCollapse" ? "铁核心失去聚变支撑" : "外层正在返回星际介质";
  if (state.stage === "remnant") return state.star?.fate === "coreCollapse" ? "中子星遗迹与抛射物" : "白矮星遗迹与抛射物";
  return STELLAR_STAGES[state.stage].name;
}

function nextStageName(stage: StellarStage) {
  const next = stageOrder[stageOrder.indexOf(stage) + 1];
  return next ? STELLAR_STAGES[next].name : "下一代";
}

function pulseExplanation(state: StellarState) {
  if (state.stage === "cloud") return "集中一次冷气体坍缩的观测窗口；质量账本不变，密度与中心温度逐步上升。";
  if (state.stage === "protostar") return "继续跟踪引力收缩供能，直到核心氢聚变足以建立长期压力平衡。";
  if (state.stage === "mainSequence") return "记录氢向氦的缓慢转化；质量不同，光度和燃料消耗速率也不同。";
  if (state.stage === "giant") return "比较核心收缩与外层膨胀；表面变冷并不意味着总光度降低。";
  return "提高终末阶段的时间分辨率，并核对遗迹质量与抛射质量。";
}

function compositionExplanation(state: StellarState) {
  if (state.stage === "cloud" || state.stage === "protostar") return "氢尚未开始稳定核心聚变；原恒星主要由引力收缩供能。";
  if (state.stage === "mainSequence") return "核心氢逐步转化为氦；向外压力与引力维持长期平衡。";
  if (state.stage === "giant") return state.star?.fate === "coreCollapse" ? "高质量路线能继续进入更高级燃烧，逐步建立更重元素核心。" : "低质量路线形成以碳氧为主的核心，并通过壳层燃烧膨胀。";
  return "账本展示简化的燃料转化顺序，不包含完整同位素与反应率网络。";
}

function fateLabel(fate: StellarFate) {
  return fate === "coreCollapse" ? "核心坍缩 → 中子星" : "外层抛射 → 白矮星";
}

function formatYears(years: number) {
  if (years >= 1_000_000_000) return `约 ${formatNumber(years / 1_000_000_000, false)} 十亿年`;
  return `约 ${formatNumber(years / 1_000_000, false)} 百万年`;
}

function formatNumber(value: number, compact: boolean) {
  return new Intl.NumberFormat("zh-CN", {
    notation: compact && Math.abs(value) >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(value) < 1 ? 4 : 1,
  }).format(value);
}

function formatPercent(value: number) {
  return `${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: value < .01 ? 2 : 1 }).format(value * 100)}%`;
}

function sameSettings(left: StellarSettings, right: StellarSettings) {
  return left.reducedMotion === right.reducedMotion && left.compactNumbers === right.compactNumbers;
}
